/** @format */

const errors = {
  user_already_exists: 'Un compte existe déjà avec cette adresse e-mail.',
  email_error: "Impossible d'envoyer l'e-mail de confirmation.",
  account_not_confirmed:
    'Veuillez confirmer votre compte via le lien reçu par e-mail.',
  account_locked: 'Ce compte est suspendu.',
  bad_credentials: 'Vos identifiants sont incorrects.',
  e_obj_not_loadable: 'Element Sellsy inconnu.',
  sellsy_error: 'Erreur Sellsy non répertoriée.',
  pvgis_horizon_error:
    "Problème de connection à PVGIS, impossible d'utiliser le masque d'ombrage.",
  update_production_error:
    'Problème de connection à PVGIS, impossible de mettre à jour des données de production.',
  file_size_is_zero: 'Oups ! Il semblerait que ce fichier soit vide !',
  no_mandate_file:
    "Impossible d'envoyer un mandat de représentation générique pour cette régie",
  cannot_send_email: "Impossible d'envoyer cet e-mail.",
  Invalid_To_header: 'Destinataire inconnu',
};

const statusErrors = {
  400: 'La requête est incomplète. (e400)',
  401: 'Requête non autorisée. (e401)',
  403: "Vous n'avez pas la permission. (e403)",
  404: "L'élément n'existe pas. (e404)",
  409: 'Un conflit est survenu. (e409)',
  413: 'Les fichiers dépassent la taille maximale autorisée. (e413)',
  429: 'Veuillez patienter entre chaque demande.',
  500: 'Erreur interne du serveur. (e500)',
  502: 'Le serveur ne répond pas. (e502)',
  503: 'Le serveur ne répond pas. (e503)',
};

const UPLOAD_TOO_BIG_MESSAGE =
  'Les fichiers dépassent la taille maximale autorisée (100 Mo).';

const responseDataLooksTooBig = (data) =>
  typeof data === 'string' &&
  (data.includes('RequestDataTooBig') ||
    data.includes('DATA_UPLOAD_MAX_MEMORY_SIZE'));

/**
 * Joins string leaves from a parsed field-error state into one snackbar message.
 * @param {*} state
 * @returns {string|null}
 */
export const flattenErrorState = (state) => {
  if (!state) return null;
  const messages = [];
  const walk = (value) => {
    if (value == null) return;
    if (typeof value === 'string') {
      if (value.trim()) messages.push(value.trim());
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (typeof value === 'object') {
      Object.values(value).forEach(walk);
    }
  };
  walk(state);
  return messages.length ? messages.join(' ') : null;
};

const priorRequestErrors = {
  APICARTO:
    'Erreur de connexion au serveur IGN, vérifiez le point GPS et réessayez. (err: APICARTO)',
  CADPLOT:
    'Impossible de trouver la parcelle correspondante, vérifiez le point GPS et réessayez. (err: CADPLOT)',
  CADCITY:
    'Le nom de la commune ne correspond pas aux données du Cadastre, veuillez contacter un administrateur. (err: CADCITY)',
  CADPAGE:
    'Une erreur est survenue lors de la récupération des données de la parcelle, vérifiez le point GPS et réessayez. (err: CADPAGE)',
  CADFORM:
    'Une erreur est survenue lors de la récupération des données de la parcelle, vérifiez le point GPS et réessayez. (err: CADFORM)',
  CADPDF:
    'Une erreur est survenue lors de la récupération du plan de situation, veuillez contacter un administrateur. (err: CADPDF)',
  GEOITO:
    'Le serveur de cartographie Géoportail IGN ne répond pas, veuillez réessayer. (err: GEOITO)',
  GEOIER:
    'Une erreur est survenue lors de la récupération des cartes Géoportail, vérifiez le point GPS et réessayez. (err: GEOIER)',
};

/**
 * Parses field error
 * @param error {Object}
 * @param generic {Boolean}
 */
const parseField = (error, generic) =>
  Object.fromEntries(
    Object.entries(error)
      .map((er) => [
        // eslint-disable-next-line no-nested-ternary
        generic
          ? 'error'
          : !/^\d+$/.test(er[0])
          ? `${er[0].replace(/_([a-z0-9])/g, (g) => g[1].toUpperCase())}Error`
          : er[0],
        er[1],
      ])
      .map((er) => {
        const newEr = er;
        if (er[1].constructor === Array) {
          if (er[1][0].constructor !== String) {
            newEr[1] = parseField(er[1]);
          } else {
            newEr[1] = er[1].join(' ');
          }
        } else if (
          er[1].constructor === Object &&
          Object.keys(er[1]).length > 0
        ) {
          newEr[1] = parseField(er[1]);
        }
        return newEr;
      }),
  );

const isJsonBlob = (data) =>
  data instanceof Blob && data.type === 'application/json';

/**
 * Parses field error
 * @param error {Object} error
 * @param generic {Boolean} generic
 */
export const parseFieldError = (error, generic = false) => {
  let message = null;
  let state = null;
  const statusCode = error.response ? error.response.status : null;
  if (error.response && error.response.data && statusCode !== 429) {
    const { data } = error.response;
    if (data instanceof ArrayBuffer) {
      let decoded = '';
      try {
        const dec = new TextDecoder('utf-8');
        decoded = dec.decode(data);
        const obj = JSON.parse(decoded);
        if (obj.message && obj.slug) {
          message = priorRequestErrors[obj.slug] || obj.message;
          return { message, state };
        }
      } catch (e) {
        if (responseDataLooksTooBig(decoded)) {
          message = UPLOAD_TOO_BIG_MESSAGE;
        } else {
          return {
            message:
              "Une erreur non répertoriée est survenue. Veuillez contacter l'équipe technique.",
            state: null,
          };
        }
      }
    }
    if (!message && typeof data === 'string') {
      if (responseDataLooksTooBig(data)) {
        message = UPLOAD_TOO_BIG_MESSAGE;
      } else {
        message = statusErrors[statusCode] || statusErrors[400];
      }
    } else if (!message) {
      if (data.prior_request) {
        message = data.prior_request;
      }
      if (!message && data.error) {
        if (!errors[data.error]) {
          message = data.error;
        } else {
          message = errors[data.error];
        }
      }
      if (!message && Object.entries(data).length > 0) {
        state = parseField(data, generic);
      }
      if (!message && !state) {
        message = statusErrors[error.response.status];
      }
    }
  } else if (error.response && error.response.status !== undefined) {
    message = statusErrors[error.response.status];
  }
  if (!message && !state) {
    message = 'Une erreur non répertoriée est survenue.';
  }
  return {
    state,
    message,
    statusCode,
  };
};

export const parseBlobError = async (error) => {
  const responseData = isJsonBlob(error?.response?.data)
    ? await error?.response?.data?.text()
    : error?.response?.data || {};
  const newError = error;
  newError.response = {
    ...newError.response,
    data: JSON.parse(responseData),
  };
  return parseFieldError(newError);
};

export default null;
